import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import ProjectExecutionPlanApi from "../../../../../infrastructure/api/projectExecutionPlan";
import { toJalaliFullDate } from "../../../../middleware/middleware";

const MILESTONE_TYPES = [
  { value: 1, label: "آماده‌سازی سایت" },
  { value: 2, label: "تامین تجهیزات" },
  { value: 3, label: "نصب" },
  { value: 4, label: "بازرسی" },
  { value: 5, label: "اتصال به شبکه" },
  { value: 6, label: "تحویل" },
  { value: 7, label: "سفارشی" },
];

const PARTY_TYPES = [
  { value: 1, label: "پیمانکار" },
  { value: 2, label: "تامین‌کننده" },
  { value: 3, label: "ناظر" },
  { value: 4, label: "سولارنت" },
];

const MILESTONE_STATUSES = [
  { value: 1, label: "در انتظار" },
  { value: 2, label: "در حال انجام" },
  { value: 3, label: "انجام شده" },
  { value: 4, label: "مسدود" },
  { value: 5, label: "لغو شده" },
];

const planColor = (statusTitle) => {
  const text = String(statusTitle || "");
  if (text.includes("پیش‌نویس")) return "default";
  if (text.includes("ارسال")) return "info";
  if (text.includes("تایید")) return "success";
  if (text.includes("اجرا")) return "primary";
  if (text.includes("تکمیل")) return "success";
  if (text.includes("لغo")) return "error";
  return "default";
};

const emptyMilestoneForm = () => ({
  title: "",
  description: "",
  milestoneType: 1,
  responsiblePartyType: 1,
  responsiblePartyId: "",
  plannedStartDate: "",
  plannedEndDate: "",
  sortOrder: 1,
  notes: "",
  isPaymentTrigger: false,
  isProcurementTrigger: false,
});

const ProjectExecutionPlanPage = ({ source = "Management" }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activations, setActivations] = useState([]);
  const [selectedActivation, setSelectedActivation] = useState(null);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [planNotes, setPlanNotes] = useState("");
  const [plannedStartDate, setPlannedStartDate] = useState("");
  const [plannedEndDate, setPlannedEndDate] = useState("");
  const [milestoneForm, setMilestoneForm] = useState(emptyMilestoneForm());

  const areaLabel = source === "Management" ? "مدیریت" : "اپراتور سولارنت";
  const basePath =
    source === "Management" ? "/SLN/Management" : "/SLN/SolarentOperator";

  const loadActivations = async () => {
    setLoading(true);
    setError("");
    const res = await ProjectExecutionPlanApi.getActiveActivationsWithoutPlan(source);
    if (!res.ok) {
      setError(res.message || "خطا در دریافت فعال‌سازی‌ها");
      setActivations([]);
    } else {
      setActivations(Array.isArray(res.data) ? res.data : []);
    }
    setLoading(false);
  };

  const loadPlan = async (activationId) => {
    if (!activationId) {
      setPlan(null);
      return;
    }
    const res = await ProjectExecutionPlanApi.getExecutionPlanByActivation(
      source,
      activationId
    );
    if (res.ok) {
      setPlan(res.data || null);
    }
  };

  useEffect(() => {
    loadActivations();
  }, [source]);

  const selectActivation = async (item) => {
    setSelectedActivation(item);
    setSuccess("");
    setError("");
    setPlannedStartDate("");
    setPlannedEndDate("");
    setPlanNotes("");
    setMilestoneForm(emptyMilestoneForm());
    await loadPlan(item.projectExecutionActivationId);
  };

  const handleCreatePlan = async () => {
    if (!selectedActivation?.projectExecutionActivationId) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    const res = await ProjectExecutionPlanApi.createDraftExecutionPlan(source, {
      projectExecutionActivationId: selectedActivation.projectExecutionActivationId,
      plannedStartDate: plannedStartDate || undefined,
      plannedEndDate: plannedEndDate || undefined,
      notes: planNotes.trim() || undefined,
    });
    if (!res.ok) {
      setError(res.message || "ایجاد برنامه ناموفق بود.");
    } else {
      setSuccess(res.message || "پیش‌نویس برنامه ایجاد شد.");
      setPlan(res.data);
      await loadActivations();
    }
    setSubmitting(false);
  };

  const handleAddMilestone = async () => {
    if (!plan?.id) return;
    if (!milestoneForm.title.trim()) {
      setError("عنوان مایلستون الزامی است.");
      return;
    }
    setSubmitting(true);
    setError("");
    setSuccess("");
    const res = await ProjectExecutionPlanApi.addExecutionMilestone(source, {
      projectExecutionPlanId: plan.id,
      title: milestoneForm.title.trim(),
      description: milestoneForm.description.trim() || undefined,
      milestoneType: Number(milestoneForm.milestoneType),
      responsiblePartyType: Number(milestoneForm.responsiblePartyType),
      responsiblePartyId: milestoneForm.responsiblePartyId
        ? Number(milestoneForm.responsiblePartyId)
        : selectedActivation?.contractorInfoId || undefined,
      plannedStartDate: milestoneForm.plannedStartDate || undefined,
      plannedEndDate: milestoneForm.plannedEndDate || undefined,
      sortOrder: Number(milestoneForm.sortOrder) || 1,
      notes: milestoneForm.notes.trim() || undefined,
      isPaymentTrigger: Boolean(milestoneForm.isPaymentTrigger),
      isProcurementTrigger: Boolean(milestoneForm.isProcurementTrigger),
    });
    if (!res.ok) {
      setError(res.message || "افزودن مایلستون ناموفق بود.");
    } else {
      setSuccess(
        res.data?.dateRangeWarning
          ? `${res.message} — هشدار: ${res.data.dateRangeWarning}`
          : res.message || "مایلستون اضافه شد."
      );
      setMilestoneForm(emptyMilestoneForm());
      await loadPlan(selectedActivation.projectExecutionActivationId);
    }
    setSubmitting(false);
  };

  const handleSubmitPlan = async () => {
    if (!plan?.id) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    const res = await ProjectExecutionPlanApi.submitExecutionPlan(source, {
      projectExecutionPlanId: plan.id,
      notes: planNotes.trim() || undefined,
    });
    if (!res.ok) {
      setError(res.message || "ارسال برنامه ناموفق بود.");
    } else {
      setSuccess(res.message || "برنامه ارسال شد.");
      await loadPlan(selectedActivation.projectExecutionActivationId);
    }
    setSubmitting(false);
  };

  const handleApprovePlan = async () => {
    if (!plan?.id) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    const res = await ProjectExecutionPlanApi.approveExecutionPlan(source, {
      projectExecutionPlanId: plan.id,
      notes: planNotes.trim() || undefined,
    });
    if (!res.ok) {
      setError(res.message || "تایید برنامه ناموفق بود.");
    } else {
      setSuccess(res.message || "برنامه تایید شد.");
      await loadPlan(selectedActivation.projectExecutionActivationId);
    }
    setSubmitting(false);
  };

  const handleChangeMilestoneStatus = async (milestone, status) => {
    setSubmitting(true);
    setError("");
    setSuccess("");
    const res = await ProjectExecutionPlanApi.changeExecutionMilestoneStatus(source, {
      milestoneId: milestone.id,
      status: Number(status),
    });
    if (!res.ok) {
      setError(res.message || "تغییر وضعیت ناموفق بود.");
    } else {
      setSuccess(res.message || "وضعیت مایلستون به‌روزرسانی شد.");
      await loadPlan(selectedActivation.projectExecutionActivationId);
    }
    setSubmitting(false);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent
          items={[
            { title: "SLN", path: "/SLN" },
            { title: areaLabel, path: basePath },
            { title: "برنامه زمان‌بندی اجرا" },
          ]}
        />
      </Grid>

      <Grid item xs={12} md={5}>
        <Card>
          <CardHeader
            title="فعال‌سازی‌های بدون برنامه"
            subheader="Activationهای Active که هنوز برنامه زمان‌بندی ندارند."
            action={
              <IconButton onClick={loadActivations} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            }
          />
          <CardContent>
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>درخواست</TableCell>
                    <TableCell>پیمانکار</TableCell>
                    <TableCell align="center">انتخاب</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        {loading ? "در حال بارگذاری..." : "موردی یافت نشد."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    activations.map((item) => (
                      <TableRow
                        key={item.projectExecutionActivationId}
                        selected={
                          selectedActivation?.projectExecutionActivationId ===
                          item.projectExecutionActivationId
                        }
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {item.requestTitle || `درخواست ${item.requestId}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            #{item.requestReferenceId} — قرارداد {item.projectContractId}
                          </Typography>
                        </TableCell>
                        <TableCell>{item.contractorTitle || "-"}</TableCell>
                        <TableCell align="center">
                          <Button size="small" onClick={() => selectActivation(item)}>
                            انتخاب
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={7}>
        <Card>
          <CardHeader title="برنامه زمان‌بندی اجرا" />
          <CardContent>
            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}
            {success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            ) : null}

            {!selectedActivation ? (
              <Typography color="text.secondary">
                یک فعال‌سازی از لیست انتخاب کنید.
              </Typography>
            ) : !plan ? (
              <Stack spacing={2}>
                <Typography variant="body2">
                  {selectedActivation.requestTitle} — قرارداد{" "}
                  {selectedActivation.projectContractId}
                </Typography>
                <TextField
                  label="تاریخ شروع برنامه"
                  type="date"
                  value={plannedStartDate}
                  onChange={(e) => setPlannedStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="تاریخ پایان برنامه"
                  type="date"
                  value={plannedEndDate}
                  onChange={(e) => setPlannedEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="یادداشت"
                  value={planNotes}
                  onChange={(e) => setPlanNotes(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={submitting}
                  onClick={handleCreatePlan}
                >
                  ایجاد پیش‌نویس برنامه
                </Button>
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  <Chip
                    label={plan.planStatusTitle || "-"}
                    color={planColor(plan.planStatusTitle)}
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {plan.plannedStartDate
                      ? toJalaliFullDate(plan.plannedStartDate)
                      : "-"}{" "}
                    تا{" "}
                    {plan.plannedEndDate ? toJalaliFullDate(plan.plannedEndDate) : "-"}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {plan.canSubmit ? (
                    <Button
                      variant="contained"
                      color="info"
                      startIcon={<SendIcon />}
                      disabled={submitting}
                      onClick={handleSubmitPlan}
                    >
                      ارسال برنامه
                    </Button>
                  ) : null}
                  {plan.canApprove ? (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleOutlineIcon />}
                      disabled={submitting}
                      onClick={handleApprovePlan}
                    >
                      تایید برنامه
                    </Button>
                  ) : null}
                </Stack>

                {plan.canEdit ? (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      افزودن مایلستون
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="عنوان"
                          value={milestoneForm.title}
                          onChange={(e) =>
                            setMilestoneForm({ ...milestoneForm, title: e.target.value })
                          }
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>نوع</InputLabel>
                          <Select
                            value={milestoneForm.milestoneType}
                            label="نوع"
                            onChange={(e) =>
                              setMilestoneForm({
                                ...milestoneForm,
                                milestoneType: e.target.value,
                              })
                            }
                          >
                            {MILESTONE_TYPES.map((x) => (
                              <MenuItem key={x.value} value={x.value}>
                                {x.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>مسئول</InputLabel>
                          <Select
                            value={milestoneForm.responsiblePartyType}
                            label="مسئول"
                            onChange={(e) =>
                              setMilestoneForm({
                                ...milestoneForm,
                                responsiblePartyType: e.target.value,
                              })
                            }
                          >
                            {PARTY_TYPES.map((x) => (
                              <MenuItem key={x.value} value={x.value}>
                                {x.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="شناسه مسئول (اختیاری)"
                          value={milestoneForm.responsiblePartyId}
                          onChange={(e) =>
                            setMilestoneForm({
                              ...milestoneForm,
                              responsiblePartyId: e.target.value,
                            })
                          }
                          fullWidth
                          helperText={
                            selectedActivation?.contractorInfoId
                              ? `پیش‌فرض پیمانکار: ${selectedActivation.contractorInfoId}`
                              : undefined
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="شروع"
                          type="date"
                          value={milestoneForm.plannedStartDate}
                          onChange={(e) =>
                            setMilestoneForm({
                              ...milestoneForm,
                              plannedStartDate: e.target.value,
                            })
                          }
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="پایان"
                          type="date"
                          value={milestoneForm.plannedEndDate}
                          onChange={(e) =>
                            setMilestoneForm({
                              ...milestoneForm,
                              plannedEndDate: e.target.value,
                            })
                          }
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          disabled={submitting}
                          onClick={handleAddMilestone}
                        >
                          افزودن مایلستون
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                ) : null}

                <Paper variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>عنوان</TableCell>
                        <TableCell>نوع / مسئول</TableCell>
                        <TableCell>بازه</TableCell>
                        <TableCell>وضعیت</TableCell>
                        <TableCell>Trigger</TableCell>
                        <TableCell align="center">عملیات</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(plan.milestones || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            مایلستونی ثبت نشده.
                          </TableCell>
                        </TableRow>
                      ) : (
                        (plan.milestones || []).map((m) => (
                          <TableRow key={m.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {m.title}
                              </Typography>
                              {m.dateRangeWarning ? (
                                <Typography variant="caption" color="warning.main">
                                  {m.dateRangeWarning}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell>
                              {m.milestoneTypeTitle}
                              <Typography variant="caption" display="block">
                                {m.responsiblePartyTypeTitle}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" display="block">
                                {m.plannedStartDate
                                  ? toJalaliFullDate(m.plannedStartDate)
                                  : "-"}
                              </Typography>
                              <Typography variant="caption" display="block">
                                {m.plannedEndDate
                                  ? toJalaliFullDate(m.plannedEndDate)
                                  : "-"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={m.statusTitle || "-"} />
                            </TableCell>
                            <TableCell>
                              {m.isPaymentTrigger ? (
                                <Chip size="small" label="Payment" sx={{ mr: 0.5 }} />
                              ) : null}
                              {m.isProcurementTrigger ? (
                                <Chip size="small" label="Procurement" />
                              ) : null}
                            </TableCell>
                            <TableCell align="center">
                              {!plan.canEdit ? (
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <Select
                                    value={m.status}
                                    onChange={(e) =>
                                      handleChangeMilestoneStatus(m, e.target.value)
                                    }
                                    disabled={submitting}
                                  >
                                    {MILESTONE_STATUSES.map((x) => (
                                      <MenuItem key={x.value} value={x.value}>
                                        {x.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Paper>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ProjectExecutionPlanPage;
